import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useContracts, useClients } from '../contexts/DataContext';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Badge } from '../components/badge';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FianzaView } from '../components/FianzaView';
import {
  getPrimaAmount,
  getEmisorNombre,
  getTipoFianza,
  getBeneficiario,
  getFechaEmision,
} from '../utils/calculations';
import { getFianzaStatus } from '../utils/convenioCompliance';
import { getEstadoFianza, ESTADO_FIANZA } from '../utils/fianzaEventos';
import { SERIES_COLORS, OTROS_COLOR } from '../utils/chartPalette';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const titleCase = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

// "AFIANZADORA SOFIMEX, S.A." reads terribly in a stat row. Short commercial
// names for the common carriers; anything unknown gets its legal suffixes
// stripped and title-cased.
const shortAfianzadora = (name) => {
  if (!name) return 'Sin afianzadora';
  const upper = name.toUpperCase();
  if (upper.includes('ASERTA')) return 'Aserta';
  if (upper.includes('SOFIMEX')) return 'Sofimex';
  if (upper.includes('CHUBB')) return 'Chubb';
  if (upper.includes('LIBERTY')) return 'Liberty';
  if (upper.includes('BERKLEY')) return 'Berkley';
  if (upper.includes('TOKIO')) return 'Tokio Marine';
  if (upper.includes('ATLAS')) return 'Atlas';
  if (upper.includes('ASECAM')) return 'Asecam';
  const stripped = name.replace(/,?\s+(S\.?A\.?P?\.?I?\.?( DE C\.?V\.?)?|S\.? DE R\.?L\.?( DE C\.?V\.?)?)\.?$/i, '');
  return titleCase(stripped);
};

const STATUS_BADGES = [
  { code: 'vigente', label: 'Vigentes', color: 'green' },
  { code: 'vencida', label: 'Vencidas', color: 'red' },
  { code: 'pendiente', label: 'Por iniciar', color: 'zinc' },
  { code: 'cancelada', label: 'Canceladas', color: 'zinc' },
  { code: 'anulada', label: 'Anuladas', color: 'zinc' },
  { code: 'sustituida', label: 'Sustituidas', color: 'zinc' },
];

const DIMENSIONS = [
  { key: 'tipo', label: 'Por tipo' },
  { key: 'afianzadora', label: 'Por afianzadora' },
  { key: 'beneficiario', label: 'Por beneficiario' },
];

// Org-wide mode (no clientId) gets the extra dimension.
const DIMENSION_CLIENTE = { key: 'cliente', label: 'Por cliente' };
// Convenio mode slices by the fiado's side instead.
const DIMENSION_PROVEEDOR = { key: 'proveedor', label: 'Por proveedor' };

const METRICS = [
  { key: 'monto', label: 'Monto afianzado', money: true },
  { key: 'prima', label: 'Prima', money: true },
  { key: 'count', label: 'Fianzas', money: false },
];
// The beneficiario's side of a convenio never sees prima — it is the fiado's
// commercial data (same rule as ContratoFianzasPanel mode='client').
const METRICS_CLIENT = METRICS.filter((m) => m.key !== 'prima');

// Past this many groups the tail folds into "Otros" — more slices/rows stop
// being comparable and start being a list. Matches the 8-slot palette.
const MAX_GROUPS = 8;

const DIAS_VENCIMIENTO = 90;

function StatTile({ label, value, sub }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-xl font-bold text-azul-surelia">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function PillGroup({ options, value, onChange }) {
  return (
    <div className="flex rounded-lg border border-azul-surelia/20 overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
            value === opt.key
              ? 'bg-azul-surelia text-white'
              : 'bg-white text-azul-surelia hover:bg-azul-surelia/5'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * ClientEstadisticas — a fianza portfolio at a glance. With a clientId it is
 * that cliente's tab in ClienteDetails; without one it aggregates the whole
 * cartera (used by the Estadísticas reporte) and gains the "Por cliente"
 * dimension.
 *
 * Distribution (donut + ranked bars) covers ACTIVE fianzas only; the historial
 * chart covers everything emitted except anuladas/sustituidas (canceladas were
 * real production), matching the reportes' rule. Colors are assigned per group
 * by monto ranking and stay stable while the metric pill changes — color
 * follows the entity, not its current rank.
 */
export function ClientEstadisticas({ clientId = null, convenioId = null, proyectoId = null, showResumen = true, selectedYear = null, mode = 'broker' }) {
  const { contractsList, contractsLoading } = useContracts();
  const { clientsById } = useClients();
  const isBroker = mode === 'broker';
  const metricOptions = isBroker ? METRICS : METRICS_CLIENT;
  const isOrgWide = !clientId && !convenioId;
  const dimensions = useMemo(
    () => (isOrgWide
      ? [DIMENSION_CLIENTE, ...DIMENSIONS]
      : convenioId
      ? [DIMENSION_PROVEEDOR, ...DIMENSIONS]
      : DIMENSIONS),
    [isOrgWide, convenioId]
  );
  const [dimension, setDimension] = useState(
    isOrgWide ? 'cliente' : convenioId ? 'proveedor' : 'tipo'
  );
  const [metric, setMetric] = useState('monto');
  const [selectedRow, setSelectedRow] = useState(null); // group key for the slide-over
  const [selectedFianza, setSelectedFianza] = useState(null); // For FianzaView

  const clientContracts = useMemo(
    () => (clientId
      ? contractsList.filter((c) => c.clientId === clientId)
      : convenioId
      ? contractsList.filter(
          (c) => c.convenioId === convenioId && (!proyectoId || c.proyectoId === proyectoId)
        )
      : contractsList),
    [contractsList, clientId, convenioId, proyectoId]
  );

  const fianzas = useMemo(() => {
    const now = Date.now();
    return clientContracts.flatMap((contract) => {
      const clienteNombre = contract.convenioId
        ? 'Convenios'
        : clientsById[contract.clientId]?.nombreEmpresa || 'Sin nombre';
      const proveedorNombre = titleCase(contract.proveedor?.value || 'Sin proveedor');
      return (contract.fianzas || []).map((fianza) => {
        const fin = fianza.finVigenciaFianza?.toMillis?.() || fianza.finVigenciaFianza || 0;
        return {
          fianza: { ...fianza, contratoId: fianza.contratoId || contract.id, clientId: contract.clientId || null },
          cliente: clienteNombre,
          clienteFull: clienteNombre,
          proveedor: proveedorNombre,
          proveedorFull: contract.proveedor?.value || 'Sin proveedor',
          estado: getEstadoFianza(fianza),
          statusCode: getFianzaStatus(fianza, now).code,
          tipo: titleCase(getTipoFianza(fianza) || 'Sin tipo'),
          afianzadora: shortAfianzadora(getEmisorNombre(fianza)),
          afianzadoraFull: getEmisorNombre(fianza) || 'Sin afianzadora',
          beneficiario: titleCase(getBeneficiario(fianza).nombre || 'Sin beneficiario'),
          beneficiarioFull: getBeneficiario(fianza).nombre || 'Sin beneficiario',
          monto: Number(fianza.montoAfianzado) || 0,
          prima: getPrimaAmount(fianza) || 0,
          fin,
          emisionMs: getFechaEmision(fianza),
        };
      });
    });
  }, [clientContracts, clientsById]);

  const activas = useMemo(
    () => fianzas.filter((f) => f.estado === ESTADO_FIANZA.ACTIVA),
    [fianzas]
  );

  // What the distribution slices over. Tab mode: the live portfolio (activas
  // today). Reporte mode (selectedYear set): fianzas EMITIDAS that year, with
  // the reportes rule — anuladas/sustituidas out, canceladas retained.
  const distribucionBase = useMemo(() => {
    if (!selectedYear) return activas;
    return fianzas.filter(
      (f) =>
        f.estado !== ESTADO_FIANZA.ANULADA &&
        f.estado !== ESTADO_FIANZA.SUSTITUIDA &&
        f.emisionMs &&
        new Date(f.emisionMs).getFullYear() === selectedYear
    );
  }, [fianzas, activas, selectedYear]);

  const statusCounts = useMemo(() => {
    const counts = {};
    fianzas.forEach((f) => {
      counts[f.statusCode] = (counts[f.statusCode] || 0) + 1;
    });
    return counts;
  }, [fianzas]);

  // Stable color assignment per dimension: ranked by monto once, independent of
  // the metric pill, so toggling prima/count never repaints a group.
  const colorMaps = useMemo(() => {
    const maps = {};
    dimensions.forEach(({ key: dim }) => {
      const totals = new Map();
      distribucionBase.forEach((f) => {
        totals.set(f[dim], (totals.get(f[dim]) || 0) + f.monto);
      });
      const ranked = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
      const map = new Map();
      ranked.forEach(([key], i) => {
        map.set(key, i < SERIES_COLORS.length ? SERIES_COLORS[i] : OTROS_COLOR);
      });
      maps[dim] = map;
    });
    return maps;
  }, [distribucionBase, dimensions]);

  const colorFor = (dim, key) =>
    key.startsWith('Otros') ? OTROS_COLOR : colorMaps[dim]?.get(key) || OTROS_COLOR;

  const rows = useMemo(() => {
    const map = new Map();
    distribucionBase.forEach((f) => {
      const key = f[dimension];
      const entry = map.get(key) || {
        key,
        fullName: dimension === 'tipo' ? key : f[`${dimension}Full`],
        count: 0,
        vigentes: 0,
        monto: 0,
        prima: 0,
        fianzas: [],
      };
      entry.count += 1;
      if (f.statusCode === 'vigente') entry.vigentes += 1;
      entry.monto += f.monto;
      entry.prima += f.prima;
      entry.fianzas.push(f);
      map.set(key, entry);
    });

    const sorted = Array.from(map.values()).sort((a, b) => b[metric] - a[metric]);
    if (sorted.length <= MAX_GROUPS) return sorted;

    const head = sorted.slice(0, MAX_GROUPS - 1);
    const tail = sorted.slice(MAX_GROUPS - 1);
    head.push(tail.reduce(
      (acc, r) => ({
        ...acc,
        count: acc.count + r.count,
        vigentes: acc.vigentes + r.vigentes,
        monto: acc.monto + r.monto,
        prima: acc.prima + r.prima,
        fianzas: acc.fianzas.concat(r.fianzas),
      }),
      { key: `Otros (${tail.length})`, fullName: null, count: 0, vigentes: 0, monto: 0, prima: 0, fianzas: [] }
    ));
    return head;
  }, [distribucionBase, dimension, metric]);

  // Historial: production per year stacked by afianzadora. Anuladas and
  // sustituidas excluded, canceladas retained — the reportes' rule. Client mode
  // stacks monto afianzado instead of prima.
  const historial = useMemo(() => {
    const valueOf = (f) => (isBroker ? f.prima : f.monto);
    const produccion = fianzas.filter(
      (f) => f.estado !== ESTADO_FIANZA.ANULADA && f.estado !== ESTADO_FIANZA.SUSTITUIDA && f.emisionMs
    );
    const porAfianzadora = new Map();
    produccion.forEach((f) => {
      porAfianzadora.set(f.afianzadora, (porAfianzadora.get(f.afianzadora) || 0) + valueOf(f));
    });
    const topAfianzadoras = Array.from(porAfianzadora.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([k]) => k);
    const hasOtros = porAfianzadora.size > topAfianzadoras.length;

    const porAnio = new Map();
    produccion.forEach((f) => {
      const year = new Date(f.emisionMs).getFullYear();
      const row = porAnio.get(year) || { year };
      const serie = topAfianzadoras.includes(f.afianzadora) ? f.afianzadora : 'Otros';
      row[serie] = (row[serie] || 0) + valueOf(f);
      porAnio.set(year, row);
    });

    return {
      data: Array.from(porAnio.values()).sort((a, b) => a.year - b.year),
      series: hasOtros ? [...topAfianzadoras, 'Otros'] : topAfianzadoras,
    };
  }, [fianzas, isBroker]);

  // Vencimientos: activas already vencidas, then the ones expiring soon.
  const vencimientos = useMemo(() => {
    const now = Date.now();
    const limite = now + DIAS_VENCIMIENTO * 86400000;
    return activas
      .filter((f) => f.fin > 0 && f.fin <= limite && (f.statusCode === 'vigente' || f.statusCode === 'vencida'))
      .sort((a, b) => a.fin - b.fin)
      .slice(0, 8);
  }, [activas]);

  const metricDef = metricOptions.find((m) => m.key === metric);
  const total = rows.reduce((s, r) => s + r[metric], 0);
  const maxValue = rows.reduce((m, r) => Math.max(m, r[metric]), 0);

  const montoTotal = activas.reduce((s, f) => s + f.monto, 0);
  const primaTotal = activas.reduce((s, f) => s + f.prima, 0);

  if (contractsLoading) {
    return <div className="py-8 text-center text-azul-surelia/70">Cargando datos…</div>;
  }

  return (
    <div className="space-y-4">
      {showResumen && (
      <>
      <div className="flex flex-col gap-1">
        <Heading level={2} className="text-xl font-bold text-azul-surelia">
          Estadísticas
        </Heading>
        <Text className="text-sm text-azul-surelia/70">
          {isOrgWide
            ? 'Portafolio de fianzas de toda la cartera.'
            : convenioId
            ? 'Portafolio de fianzas del convenio.'
            : 'Portafolio de fianzas del cliente.'}
        </Text>
      </div>

      <div className={`grid grid-cols-2 gap-3 ${isBroker ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <StatTile label="Contratos" value={clientContracts.length} />
        <StatTile
          label="Fianzas activas"
          value={activas.length}
          sub={fianzas.length !== activas.length ? `${fianzas.length} en total` : null}
        />
        <StatTile label="Monto afianzado" value={fmtMXN(montoTotal)} sub="Fianzas activas" />
        {isBroker && <StatTile label="Prima total" value={fmtMXN(primaTotal)} sub="Fianzas activas" />}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-2">
          Estado de las fianzas
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_BADGES.filter((s) => statusCounts[s.code]).map((s) => (
            <Badge key={s.code} color={s.color}>
              {s.label}: {statusCounts[s.code]}
            </Badge>
          ))}
          {fianzas.length === 0 && (
            <Text className="text-sm text-azul-surelia/70">Este cliente aún no tiene fianzas.</Text>
          )}
        </div>
      </div>

      {/* Próximos vencimientos */}
      {vencimientos.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-2">
            Vencimientos en {DIAS_VENCIMIENTO} días
          </div>
          <div className="space-y-1">
            {vencimientos.map((f) => {
              const dias = Math.ceil((f.fin - Date.now()) / 86400000);
              return (
                <button
                  key={f.fianza.id}
                  type="button"
                  onClick={() => setSelectedFianza(f.fianza)}
                  className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left rounded-lg border border-zinc-200 px-3 py-1.5 hover:border-azul-surelia/40 transition-colors"
                >
                  <span className="text-sm font-mono text-zinc-700 min-w-[130px]">
                    {f.fianza.numeroFianza || 'Sin número'}
                  </span>
                  <span className="text-xs text-zinc-500 min-w-[110px] truncate">{f.tipo}</span>
                  <span className="text-xs text-zinc-500 truncate">{f.afianzadora}</span>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ml-auto ${
                    dias < 0 ? 'bg-red-100 text-red-800' : dias <= 30 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {dias < 0 ? `Venció hace ${-dias} días` : `Vence en ${dias} días`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      </>
      )}

      {/* Distribución: donut + ranked bars */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <PillGroup
            options={dimensions}
            value={dimension}
            onChange={(d) => { setDimension(d); setSelectedRow(null); }}
          />
          <PillGroup options={metricOptions} value={metric} onChange={setMetric} />
        </div>

        {rows.length === 0 ? (
          <Text className="text-sm text-azul-surelia/70 py-4 text-center">
            {selectedYear ? `Sin fianzas emitidas en ${selectedYear}.` : 'Sin fianzas activas.'}
          </Text>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 items-center">
            <div>
              <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rows}
                    dataKey={metric}
                    nameKey="key"
                    innerRadius="62%"
                    outerRadius="95%"
                    paddingAngle={1}
                    stroke="#ffffff"
                    strokeWidth={2}
                    onClick={(entry) => setSelectedRow(selectedRow === entry.key ? null : entry.key)}
                  >
                    {rows.map((r) => (
                      <Cell key={r.key} fill={colorFor(dimension, r.key)} cursor="pointer" />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [metricDef.money ? fmtMXN(value) : value, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              </div>
              <div className="text-center mt-1">
                <div className="text-base font-bold text-azul-surelia tabular-nums">
                  {metricDef.money ? fmtMXN(total) : total}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">{metricDef.label}</div>
              </div>
            </div>

            <div>
              {rows.map((r) => {
                const value = r[metric];
                const share = total > 0 ? Math.round((value / total) * 100) : 0;
                const width = maxValue > 0 ? Math.max(2, (value / maxValue) * 100) : 0;
                const color = colorFor(dimension, r.key);
                return (
                  <div
                    key={r.key}
                    className={`cursor-pointer rounded-lg -mx-2 px-2 py-1.5 transition-colors ${
                      selectedRow === r.key ? 'bg-azul-surelia/5' : 'hover:bg-azul-surelia/5'
                    }`}
                    onClick={() => setSelectedRow(selectedRow === r.key ? null : r.key)}
                    title={isBroker ? `${fmtMXN(r.monto)} afianzado · Prima ${fmtMXN(r.prima)}` : `${fmtMXN(r.monto)} afianzado`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }}></span>
                        <span className="text-sm font-medium text-zinc-900 truncate" title={r.fullName || r.key}>
                          {r.key}
                        </span>
                        <span className="text-[11px] text-gray-500 shrink-0">
                          {r.count} fianza{r.count === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-sm font-semibold text-azul-surelia tabular-nums">
                          {metricDef.money ? fmtMXN(value) : value}
                        </span>
                        <span className="text-[11px] text-gray-400 ml-2">{share}%</span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${width}%`, backgroundColor: color }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-[10px] text-gray-400 mt-4 pt-3 border-t border-gray-100">
          {selectedYear
            ? `Fianzas emitidas en ${selectedYear}, sin anuladas ni sustituidas. Haz clic en un grupo para ver sus fianzas.`
            : 'Solo fianzas activas. Haz clic en un grupo para ver sus fianzas.'}
        </div>
      </div>

      {/* Historial de producción */}
      {historial.data.length > 0 && (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-3">
            {isBroker ? 'Prima por año de emisión' : 'Monto afianzado por año de emisión'}
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={historial.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#52514e' }} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#898781' }}
                  tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)}
                />
                <Tooltip formatter={(value, name) => [fmtMXN(value), name]} />
                {historial.series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
                {historial.series.map((serie) => (
                  <Bar
                    key={serie}
                    dataKey={serie}
                    stackId="prima"
                    fill={serie === 'Otros' ? OTROS_COLOR : colorFor('afianzadora', serie)}
                    stroke="#ffffff"
                    strokeWidth={1}
                    maxBarSize={48}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] text-gray-400 mt-2">
            Excluye anuladas y sustituidas; incluye canceladas.
          </div>
        </div>
      )}

      {/* Slide-over with the selected group's fianzas (same pattern as reportes) */}
      {selectedRow && (() => {
        const row = rows.find((r) => r.key === selectedRow);
        if (!row) return null;
        return createPortal(
          <div className="fixed inset-y-0 left-0 z-[60] w-72 flex flex-col bg-white shadow-xl border-r border-azul-surelia/20">
            <div className="bg-azul-surelia px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-white truncate" title={row.fullName || row.key}>
                    {row.key}
                  </h2>
                  <p className="text-xs text-white/80">
                    {row.count} fianza{row.count === 1 ? '' : 's'}{selectedYear ? ` de ${selectedYear}` : ` activa${row.count === 1 ? '' : 's'}`}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedRow(null)}
                  className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors ml-2"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-2">
                {row.fianzas.map((f) => (
                  <div
                    key={f.fianza.id}
                    onClick={() => setSelectedFianza(f.fianza)}
                    className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 hover:border-azul-surelia/40 hover:bg-white cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-azul-surelia truncate">
                          {f.fianza.numeroFianza || 'Sin número'}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">{f.tipo}</div>
                      </div>
                      <Badge color={f.statusCode === 'vigente' ? 'green' : f.statusCode === 'vencida' ? 'red' : 'zinc'}>
                        {f.statusCode}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div>
                        <div className="text-zinc-500">Afianzado</div>
                        <div className="font-semibold text-zinc-900">{fmtMXN(f.monto)}</div>
                      </div>
                      {isBroker && (
                        <div>
                          <div className="text-zinc-500">Prima</div>
                          <div className="font-semibold text-azul-surelia">{fmtMXN(f.prima)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* FianzaView full-screen component - rendered via portal with higher z-index */}
      {selectedFianza && createPortal(
        <div className="relative z-[70]">
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={clientsById[selectedFianza.clientId || clientId] || {}}
            contratoData={clientContracts.find((c) => c.id === selectedFianza.contratoId) || {}}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

export default ClientEstadisticas;
